import { useEffect, useState } from "react";
import { adminGet } from "../../lib/admin-api";
import { Building2, MapPin, Users, TrendingUp, Calendar } from "lucide-react";

interface PlatformStats {
  totalTenants: number;
  activeTenants: number;
  totalLocations: number;
  totalCheckIns: number;
  checkInsThisMonth: number;
  recentTenants: {
    id: string;
    name: string;
    contactEmail: string;
    plan: string;
    createdAt: string;
  }[];
  checkInsByDay: { date: string; count: number }[];
}

export function PlatformOverview() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminGet<PlatformStats>("/stats").then((res) => {
      if (res.success && res.data) setStats(res.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="p-4 sm:p-6 text-stone-500">Loading...</div>;
  if (!stats) return <div className="p-6 text-stone-500">Failed to load platform stats.</div>;

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-xl sm:text-2xl font-semibold text-stone-900 mb-4 sm:mb-6 font-display">Platform Overview</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={<Building2 size={20} />} label="Total tenants" value={stats.totalTenants} sublabel={`${stats.activeTenants} active`} color="brand" />
        <StatCard icon={<MapPin size={20} />} label="Locations" value={stats.totalLocations} sublabel="Active" color="purple" />
        <StatCard icon={<Users size={20} />} label="Check-ins this month" value={stats.checkInsThisMonth} sublabel="Current period" color="green" />
        <StatCard icon={<TrendingUp size={20} />} label="Total check-ins" value={stats.totalCheckIns} sublabel="All time" color="orange" />
        <StatCard icon={<Calendar size={20} />} label="New tenants (30d)" value={stats.recentTenants.length} sublabel="Last 30 days" color="brand" />
      </div>

      {stats.checkInsByDay.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 p-5 mb-8">
          <h2 className="text-sm font-medium text-stone-700 mb-4">Daily check-ins (last 30 days)</h2>
          <div className="flex items-end gap-[3px] h-32">
            {stats.checkInsByDay.map((day) => {
              const max = Math.max(...stats.checkInsByDay.map((d) => d.count), 1);
              const height = Math.max((day.count / max) * 100, 4);
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full bg-brand-100 rounded-sm hover:bg-brand-200 transition-colors"
                    style={{ height: `${height}%` }}
                    title={`${day.date}: ${day.count} check-ins`}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-[10px] text-stone-400">{stats.checkInsByDay[0]?.date}</span>
            <span className="text-[10px] text-stone-400">{stats.checkInsByDay[stats.checkInsByDay.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {stats.recentTenants.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <h2 className="text-sm font-medium text-stone-700 mb-4">Recent registrations</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-stone-500 border-b border-stone-100">
                  <th className="pb-3 font-medium">Name</th>
                  <th className="pb-3 font-medium">Email</th>
                  <th className="pb-3 font-medium">Plan</th>
                  <th className="pb-3 font-medium">Registered</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentTenants.map((t) => (
                  <tr key={t.id} className="border-b border-stone-50">
                    <td className="py-3 text-stone-900 font-medium">{t.name}</td>
                    <td className="py-3 text-stone-600">{t.contactEmail}</td>
                    <td className="py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.plan === "ENTERPRISE" ? "bg-purple-50 text-purple-700" : "bg-stone-100 text-stone-600"
                      }`}>
                        {t.plan}
                      </span>
                    </td>
                    <td className="py-3 text-stone-500">{new Date(t.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  sublabel,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sublabel: string;
  color: "brand" | "green" | "purple" | "orange";
}) {
  const colors = {
    brand: "bg-brand-50 text-brand-500",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-orange-50 text-orange-600",
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-5">
      <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${colors[color]} mb-3`}>
        {icon}
      </div>
      <p className="text-2xl font-semibold text-stone-900 font-display">
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      <p className="text-sm text-stone-500 mt-0.5">{label}</p>
      <p className="text-xs text-stone-400 mt-1">{sublabel}</p>
    </div>
  );
}
